import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo, faqSchema } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";
import { IntegrationDiagram } from "../components/IntegrationDiagram.js";

const ENDPOINTS: Array<{
  method: string;
  path: string;
  purpose: string;
  body: string;
}> = [
  {
    method: "POST",
    path: "/rgs/session/launch",
    purpose: "Mint a session. Server seed is committed here.",
    body: "{ operatorId, playerId, currency, locale }",
  },
  {
    method: "POST",
    path: "/rgs/round/open",
    purpose: "Debit the player wallet, bump the nonce, pre-roll the round.",
    body: "{ sessionId, betAmount }",
  },
  {
    method: "POST",
    path: "/rgs/round/resolve",
    purpose: "Cash out. Server is the clock authority.",
    body: "{ sessionId, roundId, cashoutMultiplier }",
  },
  {
    method: "POST",
    path: "/rgs/round/rollback",
    purpose: "Idempotent reversal for disputes.",
    body: "{ sessionId, roundId, reason? }",
  },
  {
    method: "GET",
    path: "/rgs/balance",
    purpose: "Play-money balance read.",
    body: "?sessionId=…",
  },
  {
    method: "GET",
    path: "/rgs/round/:id/seeds",
    purpose: "Reveal server seed after the round resolves.",
    body: "—",
  },
];

const WS_EVENTS: Array<[string, string]> = [
  ["round:state", "phase transitions — betting → live → resolved"],
  ["tick", "30 fps multiplier update"],
  ["crack", "one of five locks cracks at its scheduled time"],
  ["resolved", "final outcome — win or bust, with payout"],
];

const IFRAME_PARAMS: Array<{
  name: string;
  type: string;
  description: string;
}> = [
  {
    name: "operator",
    type: "string",
    description: "Your operator id. Must match a created operator (or 'demo' for the public sandbox).",
  },
  {
    name: "currency",
    type: "BTC | ETH | SOL | USDT | USDC | USD",
    description: "Display currency for the wallet. The session is launched in this currency.",
  },
  {
    name: "locale",
    type: "en | es | de | …",
    description: "UI language. Currently only 'en' is shipped — other locales fall back to English.",
  },
];

const POSTMESSAGE_TYPES: Array<{ type: string; payload: string; description: string }> = [
  {
    type: "SET_BALANCE",
    payload: "{ type: \"SET_BALANCE\", value: number }",
    description: "Overwrite the in-iframe wallet balance. Host page should send this on every wallet change.",
  },
  {
    type: "SET_OPERATOR",
    payload: "{ type: \"SET_OPERATOR\", value: string, locale?: string }",
    description: "Re-bind the iframe to a different operator id (rare — usually set via the URL param).",
  },
];

const FAQ_ITEMS = [
  {
    q: "How quickly can I get a sandbox key?",
    a: "Under 30 seconds. POST to /public/sandbox/operator with your company name and email, or use the form at /sandbox. Keys carry the vb_sbx_ prefix and live for 14 days.",
  },
  {
    q: "Can I deep-link the iframe to a specific operator and currency?",
    a: "Yes. Pass operator, currency, and locale as URL params on /play — e.g. /play?operator=acme&currency=USDT&locale=en. The iframe launches the session with those values.",
  },
  {
    q: "How do I move from sandbox to production?",
    a: "Submit a payout wallet via the Contact form (or PUT /admin/operators/:id/payout-wallet), countersign the MSA, and we rotate your sandbox key to a production prefix. Same endpoints, same code paths.",
  },
];

export function Integration() {
  return (
    <>
      <Seo
        title="Integration · Vault Breaker Studios"
        description="Operator integration contract for Vault Breaker Studios — REST endpoints, WebSocket stream, sandbox quick-start, iframe deep-linking."
        path="/integration"
        jsonLd={faqSchema(FAQ_ITEMS)}
      />

      <Section style={{ paddingBottom: 32 }}>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          OPERATOR REFERENCE
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Integration
        </h1>
        <p style={{ fontSize: 17, color: "var(--fg-2)", marginTop: 14, maxWidth: 720, lineHeight: 1.55 }}>
          Six REST endpoints, one WebSocket stream. Operators build the adapter in about a week.
          We never custody funds — your wallet is the source of truth, we just call into it.
        </p>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Sandbox"
          title="Need a key in 30 seconds?"
          lead="Self-serve sandbox keys are live. No call, no MSA, no waiting."
        />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
            POST <code className="mono">https://api.vaultbreaker.dev/public/sandbox/operator</code> with{" "}
            <code className="mono">{`{ companyName, email }`}</code>, or use the form at{" "}
            <Link to="/sandbox" style={{ color: "var(--cyan)" }}>/sandbox</Link>. Keys carry the{" "}
            <code className="mono">vb_sbx_</code> prefix, live for <strong>14 days</strong>, and
            you can read your operator status with <code className="mono">GET /public/sandbox/operator/me</code>.
            Rate-limited to <strong>3 keys per IP per 24 hours</strong>.
          </p>
          <pre
            style={{
              margin: 0,
              padding: "14px 18px",
              background: "var(--bg-1)",
              border: "1px solid var(--line)",
              borderRadius: 6,
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--fg-1)",
              overflow: "auto",
              whiteSpace: "pre",
              lineHeight: 1.6,
            }}
          >{`curl -X POST https://api.vaultbreaker.dev/public/sandbox/operator \\
  -H 'content-type: application/json' \\
  -d '{"companyName":"Acme Casino","email":"dev@acme.example"}'

# → { operatorId: "sbx_…", apiKey: "vb_sbx_…", expiresAt: "…+14d" }

curl https://api.vaultbreaker.dev/public/sandbox/operator/me \\
  -H 'X-API-Key: vb_sbx_…'`}</pre>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="Flow" title="Launch → open → stream → resolve → reveal" />
        <IntegrationDiagram />
      </Section>

      <Section>
        <SectionHeading eyebrow="REST" title="Endpoints" />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {ENDPOINTS.map((e, i) => (
            <div
              key={e.path}
              style={{
                padding: "18px 22px",
                borderTop: i === 0 ? "none" : "1px solid var(--line)",
                display: "grid",
                gap: 6,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    padding: "3px 8px",
                    borderRadius: 4,
                    background: e.method === "POST" ? "rgba(0,229,255,0.14)" : "rgba(0,217,126,0.14)",
                    color: e.method === "POST" ? "var(--cyan)" : "var(--green)",
                    border: `1px solid ${e.method === "POST" ? "rgba(0,229,255,0.3)" : "rgba(0,217,126,0.3)"}`,
                  }}
                >
                  {e.method}
                </span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 14, color: "var(--fg-1)" }}>
                  {e.path}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)" }}>{e.purpose}</p>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  color: "var(--fg-3)",
                }}
              >
                body · {e.body}
              </span>
            </div>
          ))}
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="WebSocket" title="/game/stream" lead="Client authenticates with the launch token, then receives server-authored events." />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {WS_EVENTS.map(([type, blurb], i) => (
            <div
              key={type}
              style={{
                padding: "16px 22px",
                borderTop: i === 0 ? "none" : "1px solid var(--line)",
                display: "grid",
                gridTemplateColumns: "160px 1fr",
                gap: 16,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 13,
                  color: "var(--cyan)",
                }}
              >
                {type}
              </span>
              <span style={{ fontSize: 13, color: "var(--fg-2)" }}>{blurb}</span>
            </div>
          ))}
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Iframe deep-link"
          title="Embed in a casino lobby"
          lead="Drop a single iframe in your lobby, deep-link with URL params, drive the wallet via postMessage."
        />
        <Card style={{ padding: 0, overflow: "hidden", marginBottom: 16 }}>
          <pre
            style={{
              margin: 0,
              padding: "20px 24px",
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              color: "var(--fg-2)",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}
          >{`<iframe
  src="https://vaultbreaker.dev/play?operator=acme&currency=USDT&locale=en"
  allow="autoplay; clipboard-read"
  style="width:100%;aspect-ratio:16/10;border:0"
></iframe>`}</pre>
        </Card>
        <SectionHeading eyebrow="URL params" title="Supported on /play" />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {IFRAME_PARAMS.map((p, i) => (
            <div
              key={p.name}
              style={{
                padding: "14px 22px",
                borderTop: i === 0 ? "none" : "1px solid var(--line)",
                display: "grid",
                gridTemplateColumns: "120px 220px 1fr",
                gap: 16,
                alignItems: "start",
              }}
            >
              <code style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--cyan)" }}>
                {p.name}
              </code>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-3)" }}>
                {p.type}
              </span>
              <span style={{ fontSize: 13, color: "var(--fg-2)", lineHeight: 1.55 }}>
                {p.description}
              </span>
            </div>
          ))}
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="postMessage bridge"
          title="Drive the iframe wallet from the host page"
          lead="The iframe accepts postMessages only from origins on its VITE_ALLOWED_PARENTS allowlist. Tell us your lobby origin during onboarding."
        />
        <Card style={{ padding: 0, overflow: "hidden", marginBottom: 16 }}>
          {POSTMESSAGE_TYPES.map((m, i) => (
            <div
              key={m.type}
              style={{
                padding: "14px 22px",
                borderTop: i === 0 ? "none" : "1px solid var(--line)",
                display: "grid",
                gap: 6,
              }}
            >
              <code style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--cyan)" }}>
                {m.type}
              </code>
              <code style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-3)" }}>
                {m.payload}
              </code>
              <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.55 }}>
                {m.description}
              </p>
            </div>
          ))}
        </Card>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <pre
            style={{
              margin: 0,
              padding: "20px 24px",
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              color: "var(--fg-2)",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}
          >{`// Host page → iframe
const frame = document.querySelector("iframe#vault");

frame.contentWindow.postMessage(
  { type: "SET_BALANCE", value: 100.0 },
  "https://vaultbreaker.dev"
);

frame.contentWindow.postMessage(
  { type: "SET_OPERATOR", value: "acme", locale: "en" },
  "https://vaultbreaker.dev"
);`}</pre>
        </Card>
        <p style={{ fontSize: 12, color: "var(--fg-3)", marginTop: 12, lineHeight: 1.55 }}>
          Note: <code>CASHOUT</code> and other player-action message types are not part of the
          host → iframe bridge — the player triggers cashouts from inside the iframe UI. Wallet
          updates flow host → iframe; round outcomes flow back via the webhook channel.
        </p>
      </Section>

      <Section>
        <SectionHeading eyebrow="Reproduce a round" title="In five lines of bash" />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <pre
            style={{
              margin: 0,
              padding: "24px 28px",
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              color: "var(--fg-2)",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}
          >
{`# Production host: https://vaultbreaker-api.fly.dev
# Local dev:       http://localhost:4000
API=https://vaultbreaker-api.fly.dev

SESSION=$(curl -s -X POST $API/rgs/session/launch \\
  -H 'content-type: application/json' \\
  -H 'x-api-key: <YOUR_API_KEY>' \\
  -d '{"operatorId":"acme","playerId":"p1","currency":"BTC"}' | jq -r .sessionId)

ROUND=$(curl -s -X POST $API/rgs/round/open \\
  -H 'content-type: application/json' \\
  -H "authorization: Bearer $TOKEN" \\
  -d "{\\"sessionId\\":\\"$SESSION\\",\\"betAmount\\":0.001}" | jq -r .roundId)

curl -s -X POST $API/rgs/round/resolve \\
  -H 'content-type: application/json' \\
  -H "authorization: Bearer $TOKEN" \\
  -d "{\\"sessionId\\":\\"$SESSION\\",\\"roundId\\":\\"$ROUND\\",\\"cashoutMultiplier\\":1.5}"

curl -s $API/rgs/round/$ROUND/seeds
curl -s -X POST $API/rgs/verify -H 'content-type: application/json' -d @seeds.json`}
          </pre>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Webhooks"
          title="Round + settlement callbacks"
          lead="Asynchronous server-to-operator deliveries for round.opened, round.resolved, session.launched, and settlement.recorded."
        />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
            Register your endpoint with <code className="mono">POST /admin/operators/:id/webhooks</code>.
            Each delivery is HMAC-SHA256 signed via <code className="mono">X-VB-Signature</code>,
            tagged with the event type, and idempotent on <code className="mono">X-VB-Delivery</code>.
            See the <Link to="/webhooks" style={{ color: "var(--cyan)" }}>webhooks page</Link> for
            payload schemas and verification snippets.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Production handover"
          title="From sandbox to live traffic"
          lead="When you're ready to flip from vb_sbx_ to a production key, three things happen."
        />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <Step
            n="1"
            title="Submit a payout wallet"
            body="Send your settlement wallet (USDT/USDC preferred) via the Contact form, or PUT /admin/operators/:id/payout-wallet directly. We use this address for every weekly settlement payout."
          />
          <Step
            n="2"
            title="Countersign the MSA"
            body="Standard B2B Master Services Agreement: 10% revenue share, weekly settlement, no minimum volume. We send the doc; you sign and return."
          />
          <Step
            n="3"
            title="Key rotation"
            body="We issue a new key with the production prefix (vb_live_…). Same endpoints, same code — you swap the env var and you're live. Sandbox keys keep working until their TTL expires."
          />
        </Card>
      </Section>

      <Section>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/contact" style={{ textDecoration: "none" }}>
            <Button variant="gold" style={{ padding: "14px 22px", fontSize: 15 }}>
              CONTACT SALES
            </Button>
          </Link>
          <Link to="/play" style={{ textDecoration: "none" }}>
            <Button style={{ padding: "14px 22px", fontSize: 15 }}>Try the demo</Button>
          </Link>
        </div>
      </Section>
    </>
  );
}

function Step({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div
      style={{
        padding: "18px 22px",
        borderTop: "1px solid var(--line)",
        display: "grid",
        gridTemplateColumns: "40px 1fr",
        gap: 16,
        alignItems: "start",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 22,
          color: "var(--gold-bright)",
          lineHeight: 1,
        }}
      >
        {n}
      </span>
      <div>
        <span
          className="label"
          style={{ display: "block", fontSize: 12, color: "var(--fg-1)", marginBottom: 6 }}
        >
          {title}
        </span>
        <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.6 }}>{body}</p>
      </div>
    </div>
  );
}
